import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PackageMinus, PackagePlus, Scissors, Search, Undo2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth, hasRole } from "@/context/AuthContext";
import { viewFor } from "@/config/roleViews";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLiveRefresh } from "@/lib/live";
import JobLink from "@/components/JobLink";
import { EmptyRow, ORANGE_BTN, PAGE_SIZE, Pager, Panel, Pill, RollsLink, SubTabs, money, qty, unit } from "@/components/stores/storesUi";
import { IssueDialog, ReceiptDialog, ReturnDialog, SlittingDialog } from "@/components/stores/StoreDialogs";

const TYPES = {
  receipt: { label: "Receipts", txn: "receipt", action: "New receipt", icon: PackagePlus, roles: ["production_manager", "accounts"] },
  issue: { label: "Issues", txn: "issue", action: "Issue material", icon: PackageMinus, roles: ["production_manager"] },
  return: { label: "Returns", txn: "return", action: "New return", icon: Undo2, roles: ["production_manager"] },
  slitting: { label: "Slitting", txn: "slit_in", action: "Slit a roll", icon: Scissors, roles: ["production_manager"] },
};

const Item = ({ e }) => (
  <>
    <RollsLink q={e.item_code} className="font-mono-num text-xs" />
    <div className="text-[11px] text-neutral-500 truncate max-w-[13rem]">{e.item_name}</div>
  </>
);

const Doc = ({ e }) => (
  <>
    <div className="font-mono-num text-xs">{e.bpr_no ? <JobLink number={e.bpr_no} tab="materials" /> : e.doc_no || "—"}</div>
    {e.bpr_no && e.doc_no && e.doc_no !== e.bpr_no && <div className="text-[11px] text-neutral-500 font-mono-num">{e.doc_no}</div>}
  </>
);

const Roll = ({ no }) => (no ? <RollsLink q={no} className="font-mono-num text-xs" /> : <span className="font-mono-num text-xs">—</span>);

const Q = ({ v, u }) => (
  <span className={v < 0 ? "text-rose-700" : ""}>{qty(v)} <span className="text-[10px] text-neutral-500">{u}</span></span>
);

function columns(type, showMoney) {
  const cols = {
    receipt: [
      ["Date", (e) => <span className="font-mono-num text-xs">{e.date}</span>],
      ["GRN", (e) => <span className="font-mono-num text-xs">{e.doc_no}</span>],
      ["Supplier", (e) => <><div className="text-xs truncate max-w-[11rem]">{e.supplier || "—"}</div><div className="text-[11px] text-neutral-500 font-mono-num">{e.invoice_no ? `inv ${e.invoice_no}` : ""}</div></>],
      ["Item", (e) => <Item e={e} />],
      ["Roll", (e) => <><Roll no={e.roll_no} /><div className="text-[11px] text-neutral-500 font-mono-num">lot {e.master_lot || "—"}</div></>],
      ["Received", (e) => <Q v={e.received_qty} u={e.uom === "KG" ? "kg" : "SQM"} />, "right"],
      ["Stock qty", (e) => <Q v={e.qty} u={unit(e.uom)} />, "right"],
      ["QC", (e) => <><Pill value={e.qc_status} />{e.qc_ar_no && <div className="text-[11px] text-neutral-500 font-mono-num mt-0.5">AR {e.qc_ar_no}</div>}</>],
      showMoney && ["Amount", (e) => <span className="font-mono-num">{money(e.amount)}</span>, "right"],
    ],
    issue: [
      ["Date", (e) => <span className="font-mono-num text-xs">{e.date}</span>],
      ["Issue no.", (e) => <Doc e={e} />],
      ["Job no.", (e) => <span className="font-mono-num text-xs">{e.job_no || "—"}</span>],
      ["Issued to", (e) => <span className="text-xs">{e.department || "—"}</span>],
      ["Item", (e) => <Item e={e} />],
      ["Roll", (e) => <Roll no={e.roll_no} />],
      ["Qty", (e) => <Q v={e.qty} u={unit(e.uom)} />, "right"],
      ["SQM", (e) => <span className="font-mono-num">{qty(e.sqm)}</span>, "right"],
      showMoney && ["Value", (e) => <span className="font-mono-num">{money(e.issue_value)}</span>, "right"],
      ["Remarks", (e) => <span className="text-xs text-neutral-600 inline-block truncate max-w-[16rem] align-bottom" title={e.remarks || ""}>{e.remarks || ""}</span>],
    ],
    return: [
      ["Date", (e) => <span className="font-mono-num text-xs">{e.date}</span>],
      ["Return no.", (e) => <Doc e={e} />],
      ["Job no.", (e) => <span className="font-mono-num text-xs">{e.job_no || "—"}</span>],
      ["From", (e) => <span className="text-xs">{e.department || "—"}</span>],
      ["Item", (e) => <Item e={e} />],
      ["Roll", (e) => <Roll no={e.roll_no} />],
      ["Qty", (e) => <Q v={e.qty} u={unit(e.uom)} />, "right"],
      ["Reason", (e) => <span className="text-xs text-neutral-600 inline-block truncate max-w-[16rem] align-bottom" title={e.reason || ""}>{e.reason || ""}</span>],
    ],
    slitting: [
      ["Date", (e) => <span className="font-mono-num text-xs">{e.date}</span>],
      ["Slitting no.", (e) => <span className="font-mono-num text-xs">{e.doc_no || "—"}</span>],
      ["Parent roll", (e) => <><Roll no={e.parent_roll} /><div className="text-[11px] text-neutral-500 font-mono-num">{e.parent_item_code ? `${e.parent_item_code} · ${qty(e.parent_length)} m` : ""}</div></>],
      ["Child roll", (e) => <Roll no={e.roll_no} />],
      ["Child item", (e) => <Item e={e} />],
      ["Length", (e) => <Q v={e.qty} u={unit(e.uom)} />, "right"],
      ["Operator", (e) => <span className="text-xs">{e.operator || "—"}</span>],
    ],
  };
  return cols[type].filter(Boolean);
}

export default function MovementsTab({ initialType = "receipt", initialBpr = "", initialQ = "", initialFrom = "", initialTo = "" }) {
  const { user } = useAuth();
  const isAdmin = hasRole(user, "admin");
  const showMoney = viewFor(user).finance;
  const [type, setType] = useState(TYPES[initialType] ? initialType : "receipt");
  const [data, setData] = useState(null);
  const [q, setQ] = useState(initialQ);
  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(initialTo);
  const [skip, setSkip] = useState(0);
  const [dialog, setDialog] = useState(initialBpr && initialType === "issue" ? "issue" : null);

  const cfg = TYPES[type];
  const canPost = hasRole(user, ...cfg.roles);
  const cols = columns(type, showMoney);
  // A slitting is corrected as a whole (parent + children), not per child row.
  const canReverse = isAdmin && type !== "slitting";

  const load = (at = skip, quiet = false) => {
    const params = { txn_type: cfg.txn, skip: at, limit: PAGE_SIZE };
    if (q.trim()) params.q = q.trim();
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    return api.get("/stores/ledger", { params })
      .then(({ data }) => setData(data))
      .catch((e) => !quiet && toast.error(formatApiError(e)));
  };
  const search = () => { setSkip(0); load(0); };
  useEffect(() => { setData(null); search(); }, [type, from, to]); // eslint-disable-line react-hooks/exhaustive-deps
  const reload = useLiveRefresh("stores", () => load(skip, true));

  const reverse = async (e) => {
    if (!window.confirm(`Reverse this ${cfg.label.toLowerCase().replace(/s$/, "")} (${e.roll_no || e.item_code}, ${qty(e.qty)})? A correcting entry is posted; nothing is deleted.`)) return;
    try {
      await api.post(`/stores/ledger/${e.id}/reverse`);
      toast.success("Entry reversed");
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // New entries land on the first page, newest first.
  const done = () => { setDialog(null); setSkip(0); reload(); };
  const Icon = cfg.icon;

  return (
    <div className="space-y-4">
      <SubTabs value={type} onChange={setType} testid="movements-type"
        options={Object.entries(TYPES).map(([value, t]) => ({ value, label: t.label }))} />

      <div className="border border-neutral-200 bg-white p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-56">
          <label className="label-caps block mb-1">Search</label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
            <Input data-testid="movements-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
              className="pl-8 rounded-sm" placeholder="Roll, item, BPR, GRN, supplier, invoice…" />
          </div>
        </div>
        <div><label className="label-caps block mb-1">From</label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-sm w-40" /></div>
        <div><label className="label-caps block mb-1">To</label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-sm w-40" /></div>
        <Button variant="outline" className="rounded-sm" onClick={search}>Apply</Button>
        {canPost && (
          <Button data-testid={`movements-new-${type}`} className={`${ORANGE_BTN} ml-auto`} onClick={() => setDialog(type)}>
            <Icon size={13} /> {cfg.action}
          </Button>
        )}
      </div>

      <Panel>
        <div className="overflow-x-auto">
          <table className="w-full text-sm [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap" data-testid={`movements-table-${type}`}>
            <thead className="bg-neutral-50 label-caps text-[10px]">
              <tr>
                {cols.map(([h, , align]) => <th key={h} className={`${align === "right" ? "text-right" : "text-left"} px-3 py-2.5 whitespace-nowrap`}>{h}</th>)}
                {canReverse && <th className="w-16"></th>}
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).length === 0 && <EmptyRow cols={cols.length + 1} loading={!data}>No entries.</EmptyRow>}
              {(data?.items || []).map((e) => (
                <tr key={e.id} className={`border-t border-neutral-100 data-row ${e.reverses || e.reversed_by ? "text-neutral-400" : ""}`}>
                  {cols.map(([h, render, align]) => (
                    <td key={h} className={`px-3 py-2 align-top ${align === "right" ? "text-right" : ""}`}>{render(e)}</td>
                  ))}
                  {canReverse && (
                    <td className="px-3 py-2 text-right align-top">
                      {e.reverses ? <span className="text-[10px] uppercase tracking-widest">reversal</span>
                        : e.reversed_by ? <span className="text-[10px] uppercase tracking-widest">reversed</span>
                        : <button className="text-xs text-neutral-500 hover:text-rose-600" onClick={() => reverse(e)}>Reverse</button>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data && <Pager skip={data.skip} limit={data.limit} total={data.total} onChange={(s) => { setSkip(s); load(s); }} />}
      </Panel>

      {dialog === "receipt" && <ReceiptDialog onClose={() => setDialog(null)} onDone={done} />}
      {dialog === "issue" && <IssueDialog initialBpr={initialBpr} onClose={() => setDialog(null)} onDone={done} />}
      {dialog === "return" && <ReturnDialog onClose={() => setDialog(null)} onDone={done} />}
      {dialog === "slitting" && <SlittingDialog onClose={() => setDialog(null)} onDone={done} />}
    </div>
  );
}
